<template>
  <div>

    <!-- Skeleton Loading State -->
    <div v-if="loading" class="skeleton-dashboard">

      <!-- Skeleton Stat Cards -->
      <div class="row mb-3">
        <div class="col-6 col-lg" v-for="n in (this.$store.state.auth.isSuperAdmin ? 4 : 3)" :key="'skel-stat-' + n">
          <div class="stat-card skeleton-card">
            <div class="skeleton-icon skeleton-shimmer"></div>
            <div class="stat-info" style="flex:1;">
              <div class="skeleton-text skeleton-text-lg skeleton-shimmer"></div>
              <div class="skeleton-text skeleton-text-sm skeleton-shimmer" style="margin-top:8px;"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Skeleton Charts Row 1 -->
      <div class="row mb-3">
        <div class="col-lg-7">
          <div class="chart-card skeleton-card">
            <div class="chart-header">
              <div class="skeleton-text skeleton-text-md skeleton-shimmer" style="width:200px;"></div>
              <div class="skeleton-text skeleton-text-sm skeleton-shimmer" style="width:100px;"></div>
            </div>
            <div class="chart-body">
              <div class="skeleton-chart skeleton-shimmer" style="height:290px;"></div>
            </div>
          </div>
        </div>
        <div class="col-lg-5">
          <div class="chart-card skeleton-card">
            <div class="chart-header">
              <div class="skeleton-text skeleton-text-md skeleton-shimmer" style="width:160px;"></div>
            </div>
            <div class="chart-body" style="display:flex; align-items:center; justify-content:center; min-height:290px;">
              <div class="skeleton-circle skeleton-shimmer"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Skeleton Charts Row 2 -->
      <div class="row">
        <div class="col-lg-7">
          <div class="chart-card skeleton-card">
            <div class="chart-header">
              <div class="skeleton-text skeleton-text-md skeleton-shimmer" style="width:140px;"></div>
              <div class="skeleton-text skeleton-text-sm skeleton-shimmer" style="width:100px;"></div>
            </div>
            <div class="chart-body">
              <div class="skeleton-chart skeleton-shimmer" style="height:265px;"></div>
            </div>
          </div>
        </div>
        <div class="col-lg-5">
          <div class="chart-card skeleton-card">
            <div class="chart-header">
              <div class="skeleton-text skeleton-text-md skeleton-shimmer" style="width:160px;"></div>
            </div>
            <div class="chart-body" style="padding: 0 16px 16px;">
              <div class="skeleton-table-row skeleton-shimmer" v-for="n in 4" :key="'skel-row-' + n" :style="{ 'animation-delay': (n * 0.08) + 's' }"></div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Actual Dashboard Content -->
    <transition name="dashboard-fade">
    <div v-if="!loading">

    <!-- Stats Summary Cards -->
    <div class="row mb-3">
      <div class="col-6 col-lg">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #00a0c1, #0088a3);">
            <i class="fa-solid fa-globe"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ totalAllowed }}</span>
            <span class="stat-label">Allowed</span>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #f3994d, #e67e22);">
            <i class="fa-solid fa-ban"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ totalBlocked }}</span>
            <span class="stat-label">Blocked</span>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #6c5ce7, #5a4bd1);">
            <i class="fa-solid fa-cloud-arrow-up"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ formattedUploads }}</span>
            <span class="stat-label">{{ totalFileCount }} file{{ totalFileCount !== 1 ? 's' : '' }} uploaded</span>
          </div>
        </div>
      </div>
      <div v-if="this.$store.state.auth.isSuperAdmin" class="col-6 col-lg">
        <div class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #e74c3c, #c0392b);">
            <i class="fa-solid fa-flag"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ reportedWebsites.length }}</span>
            <span class="stat-label">Reported</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row 1 -->
    <div class="row mb-3">
      <div class="col-lg-7">
        <div class="chart-card">
          <div class="chart-header">
            <h6 class="chart-title">
              <i class="fa-solid fa-chart-bar me-2" style="color:#00a0c1;"></i>
              Blocked &amp; Allowed Websites
            </h6>
            <select disabled v-model="filterByDate" class="form-select form-select-sm shadow-none"
              style="width:auto; border-radius: 8px; font-size: 13px;">
              <option v-for="filter in filterDate" :value="filter.value" :key="filter.value">{{ filter.text }}</option>
            </select>
          </div>
          <div class="chart-body">
            <div v-if="isLogsEmpty" class="empty-state">
              <i class="fa-solid fa-chart-bar empty-state-icon"></i>
              <span class="empty-state-text">No data to display</span>
              <span class="empty-state-subtext">Browsing activity will appear here once recorded</span>
            </div>
            <canvas v-else style="max-height: 290px;" ref="block_and_allowed_chart" id="blocked-and-allowed-chart"></canvas>
          </div>
        </div>
      </div>
      <div class="col-lg-5">
        <div class="chart-card">
          <div class="chart-header">
            <h6 class="chart-title">
              <i class="fa-solid fa-chart-pie me-2" style="color:#00a0c1;"></i>
              Top Five Websites
            </h6>
          </div>
          <div class="chart-body" style="min-height: 290px;">
            <div v-if="isTopFiveEmpty" class="empty-state">
              <i class="fa-solid fa-chart-pie empty-state-icon"></i>
              <span class="empty-state-text">No data to display</span>
              <span class="empty-state-subtext">Top visited websites will appear here</span>
            </div>
            <canvas v-else style="max-height: 290px;" ref="top_five_chart" id="top-five-data-chart"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row 2 -->
    <div class="row">
      <div class="col-lg-7">
        <div class="chart-card">
          <div class="chart-header">
            <h6 class="chart-title">
              <i class="fa-solid fa-cloud-arrow-up me-2" style="color:#00a0c1;"></i>
              Uploaded Data
            </h6>
            <select disabled v-model="filterMeasureBy" class="form-select form-select-sm shadow-none"
              style="width:auto; border-radius: 8px; font-size: 13px;">
              <option v-for="filter in filterMeasure" :value="filter.value" :key="filter.value">{{ filter.text }}</option>
            </select>
          </div>
          <div class="chart-body" style="min-height: 275px;">
            <div v-if="isUploadsEmpty" class="empty-state">
              <i class="fa-solid fa-cloud-arrow-up empty-state-icon"></i>
              <span class="empty-state-text">No data to display</span>
              <span class="empty-state-subtext">Upload activity will appear here once detected</span>
            </div>
            <canvas v-else style="max-height: 265px;" ref="upload_data_chart" id="uploaded-data-chart"></canvas>
          </div>
        </div>
      </div>
      <div class="col-lg-5">
        <div v-if="this.$store.state.auth.isSuperAdmin" class="chart-card">
          <div class="chart-header">
            <h6 class="chart-title">
              <i class="fa-solid fa-flag me-2" style="color:#e74c3c;"></i>
              Reported Websites
            </h6>
          </div>
          <div class="chart-body" style="max-height: 320px; overflow-y: auto; padding: 0 16px 16px;">
            <table class="table table-sm align-middle mb-0" style="font-size: 13px;">
              <thead class="table-light" style="position: sticky; top: 0; z-index: 1;">
                <tr>
                  <th scope="col" v-for="header in headers" :key="header.value">{{ header.text }}</th>
                </tr>
              </thead>

               <tbody v-if="reportedWebsites.length <= 0">
                <tr>
                  <td colspan="4" class="text-muted text-center py-4">No reported websites.</td>
                </tr>
              </tbody>

              <tbody>
                <tr v-for="(reportedWebsite, index) in reportedWebsites" :key="index">
                  <td :title='(reportedWebsite.account_name != null) ? reportedWebsite.account_name : ""'>
                    {{ truncateText((reportedWebsite.account_name != null) ? reportedWebsite.account_name : "", 11) }}
                  </td>
                  <td :title="trimQuestionMark(reportedWebsite.website)">
                    {{ truncateText(trimQuestionMark(reportedWebsite.website), 11) }}
                  </td>
                  <td :title="reportedWebsite.api_key">
                    {{ truncateText(reportedWebsite.api_key, 10) }}
                  </td>
                  <td>
                    <div class="d-flex gap-1">
                      <button type="button" class="ms-action-btn ms-action-btn--success"
                        @click="markStatus(reportedWebsite, 1)" title="Mark as Safe">
                        <i class="fa-solid fa-check"></i>
                      </button>
                      <button type="button" class="ms-action-btn ms-action-btn--delete"
                        @click="markStatus(reportedWebsite, 0)" title="Mark as Unsafe">
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    </div>
    </transition>

  </div>
</template>

<script>
import Chart from 'chart.js'
import moment from 'moment'
import blockedAndallowedDataChart from './blocked-and-allowed-data.js'
import uploadedDataChart from './uploaded-data.js'
import { extractErrorMessage } from '@/plugins/helper.js'

export default {
  name: 'PlanetChart',
  data() {
    return {
      headers: [
        { text: "Account", value: "accountName" },
        { text: "Website", value: "website" },
        { text: "Key", value: "extKey" },
        { text: "Actions", value: "actions" },
      ],
      blockedAndallowedDataChart: blockedAndallowedDataChart,
      uploadedDataChart: uploadedDataChart,
      labels_blocked_allowed_websites: [],
      labels_file_size: [],
      reportedWebsites: [],
      topFiveData: null,
      blockedWebsiteData: null,
      allowedWebsiteData: null,
      fileSizeData: null,
      totalBlocked: 0,
      totalAllowed: 0,
      totalUploads: 0,
      totalFileCount: 0,
      loading: true,
      rawTopFiveResp: null,
      rawUploadResp: null,
      rawLogsResp: null,
      useDummyData: false,
      filterMeasure: [
        { text: "Kilobytes", value: 0 },
        { text: "Megabytes", value: 1 },
      ],
      filterMeasureBy: 0,
      filterDate: [
        { text: "Daily", value: 0 },
        { text: "Month", value: 1 },
      ],
      filterByDate: 0,
    }
  },
  props: {
    label: {
      type: Array
    },
    chartData: {
      type: Array
    },
  },

  computed: {
    formattedUploads() {
      if (this.totalUploads >= 1000000) {
        const val = this.totalUploads / 1000000;
        const rounded = Math.ceil(val * 1000) / 1000;
        return parseFloat(rounded.toFixed(3)) + ' GB';
      }
      if (this.totalUploads >= 1000) {
        const val = this.totalUploads / 1000;
        const rounded = Math.ceil(val * 1000) / 1000;
        return parseFloat(rounded.toFixed(3)) + ' MB';
      }
      const rounded = Math.ceil(this.totalUploads * 1000) / 1000;
      return parseFloat(rounded.toFixed(3)) + ' KB';
    },
    isLogsEmpty() {
      if (!this.rawLogsResp) return true
      const d = this.rawLogsResp.data
      if (!d) return true
      const hasAllow = d.allow_count && d.allow_count.some(v => v > 0)
      const hasBlock = d.block_count && d.block_count.some(v => v > 0)
      return !hasAllow && !hasBlock
    },
    isTopFiveEmpty() {
      if (!this.rawTopFiveResp) return true
      const d = this.rawTopFiveResp.data
      if (!d) return true
      return !d.accessed_times || !d.accessed_times.some(v => v > 0)
    },
    isUploadsEmpty() {
      if (!this.rawUploadResp) return true
      const d = this.rawUploadResp.data
      if (!d) return true
      return !d.file_count || !d.file_count.some(v => v > 0)
    },
  },

  mounted() {
    if (this.$store.state.auth.isAuthenticated == false) {
      this.$store.dispatch('auth/logout');
    }

    if (this.$store.state.auth.isSuperAdmin) {
      this.getXSurflogOrganisations()
    }

    this.loadDashboardData()
  },

  methods: {
    async loadDashboardData() {
      this.loading = true
      try {
        await Promise.all([
          this.fetchFileUploadData(),
          this.fetchTopFiveData(),
          this.fetchLogsData(),
          this.getReportedWebsites(),
        ])
      } catch (e) {
        console.error('Dashboard load error:', e)
      } finally {
        this.loading = false
        // Wait for DOM to render the canvas elements before painting charts
        this.$nextTick(() => {
          this.renderTopFiveChart()
          this.renderUploadChart()
          this.renderLogsChart()
        })
      }
    },

    formatDate(date) {
      return moment.utc(date).local().format("DD-MMM-YYYY @ HH:mm:ss")
    },

    formatChartLabel(date) {
      return moment.utc(date).local().format("DD-MMM")
    },

    getMonth(monthInt) {
      switch (monthInt) {
        case 0: return "January";
        case 1: return "February";
        case 2: return "March";
        case 3: return "April";
        case 4: return "May";
        case 5: return "June";
        case 6: return "July";
        case 7: return "August";
        case 8: return "September";
        case 9: return "October";
        case 10: return "November";
        case 11: return "December";
      }
    },

    wait(ms) {
      var start = new Date().getTime();
      var end = start;
      while (end < start + ms) {
        end = new Date().getTime();
      }
    },

    async GetAccountBlockData() {
      try {
        let resp = null
        resp = await this.$axios.get("dashboard/" + this.$store.state.auth.organisation_uuid + "/get-account-blocked-data")

        if (resp.data.success != null && resp.data.success == false) {
          this.blockedWebsiteData = null
        } else {
          this.blockedWebsiteData = resp
        }

      } catch (error) {
        console.log(error)
      }
    },

    async GetAccountAllowData() {
      try {
        let resp = null
        resp = await this.$axios.get("dashboard/" + this.$store.state.auth.organisation_uuid + "/get-account-allowed-data")

        if (resp.data.success != null && resp.data.success == false) {
          this.allowedWebsiteData = null
        } else {
          this.allowedWebsiteData = resp
        }

      } catch (error) {
        console.log(error)
      }
    },

    async fetchTopFiveData() {
      try {
        let resp = null

        if (this.useDummyData) {
          resp = { data: {
            labels: ['google.com', 'youtube.com', 'github.com', 'stackoverflow.com', 'reddit.com'],
            accessed_times: [142, 98, 76, 54, 41]
          }}
        } else {
          resp = await this.$axios.get("dashboard/" + this.$store.state.auth.organisation_uuid + "/get-account-top-five-websites")
        }

        if (resp.data.success != null && resp.data.success == false) {
          this.topFiveData = null
        } else {
          this.topFiveData = resp
        }

        if (this.topFiveData != null) {
          if (this.topFiveData.labels == null) {
            this.topFiveData.labels = ["Empty data"]
          }
          if (this.topFiveData.file_count == null) {
            this.topFiveData.accessed_times = [0]
          }
        }

        this.rawTopFiveResp = resp

      } catch (error) {
        console.log(error)
      }
    },

    renderTopFiveChart() {
      if (!this.rawTopFiveResp || !this.$refs.top_five_chart) return
      const resp = this.rawTopFiveResp

      new Chart(this.$refs.top_five_chart, {
          type: 'doughnut',
          data: {
            labels: resp.data.labels,
            datasets: [{
              label: 'Top five websites',
              data: resp.data.accessed_times,
              backgroundColor: [
                '#00A0C1',
                '#F3994D',
                '#6c5ce7',
                '#00b894',
                '#e74c3c'
              ],
              hoverBackgroundColor: [
                '#008da8',
                '#e88a3d',
                '#5a4bd1',
                '#00a381',
                '#c0392b'
              ],
              borderWidth: 3,
              borderColor: '#fff',
            }]
          },
          options: {
            cutoutPercentage: 68,
            animation: {
              animateRotate: true,
              animateScale: true,
              duration: 800,
              easing: 'easeOutQuart'
            },
            legend: {
              position: 'bottom',
              labels: {
                padding: 14,
                fontSize: 12,
                fontColor: '#435666',
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltips: {
              backgroundColor: 'rgba(44, 62, 80, 0.9)',
              titleFontSize: 13,
              bodyFontSize: 12,
              cornerRadius: 8,
              xPadding: 12,
              yPadding: 10,
              displayColors: true,
              callbacks: {
                label: function(tooltipItem, data) {
                  var label = data.labels[tooltipItem.index] || '';
                  var value = data.datasets[0].data[tooltipItem.index];
                  var total = data.datasets[0].data.reduce(function(a, b) { return a + b; }, 0);
                  var pct = total > 0 ? Math.round((value / total) * 100) : 0;
                  return ' ' + label + ': ' + value + ' (' + pct + '%)';
                }
              }
            }
          }
        })
    },


    async fetchFileUploadData() {
      try {
        let resp = null

        if (this.useDummyData) {
          resp = { data: {
            labels: ['14-Apr', '15-Apr', '16-Apr', '17-Apr', '18-Apr', '19-Apr', '20-Apr', '21-Apr', '22-Apr', '23-Apr'],
            file_count: [120, 245, 180, 310, 275, 420, 380, 190, 350, 290],
            total_file_count: 147
          }}
        } else {
          resp = await this.$axios.get("dashboard/" + this.$store.state.auth.organisation_uuid + "/get-account-file-upload-data")
        }

        if (resp.data.success != null && resp.data.success == false) {
          this.fileSizeData = null
        } else {
          this.fileSizeData = resp.data
        }

        if (this.fileSizeData != null) {
          if (this.fileSizeData.labels != null) {
            this.fileSizeData.labels = ["", ...this.fileSizeData.labels.map(l => this.formatChartLabel(l))]
          }
          if (this.fileSizeData.file_count != null) {
            this.fileSizeData.file_count = [0, ...this.fileSizeData.file_count]
            this.totalUploads = this.fileSizeData.file_count.reduce((a, b) => a + b, 0)
          }
          if (resp.data.total_file_count != null) {
            this.totalFileCount = resp.data.total_file_count
          }
        }

        if (this.fileSizeData != null) {
          if (this.fileSizeData.labels == null) {
            this.fileSizeData.labels = ["Empty data"]
          }
          if (this.fileSizeData.file_count == null) {
            this.fileSizeData.file_count = [0]
          }
        }

        this.rawUploadResp = resp

      } catch (error) {
        console.log(error)
      }
    },

    renderUploadChart() {
      if (!this.rawUploadResp || !this.$refs.upload_data_chart) return
      const resp = this.rawUploadResp
      let fc = resp.data.file_count.length

      var uploadCtx = this.$refs.upload_data_chart.getContext('2d');
      var uploadGrad = uploadCtx.createLinearGradient(0, 0, 0, 265);
      uploadGrad.addColorStop(0, 'rgba(0, 160, 193, 0.25)');
      uploadGrad.addColorStop(1, 'rgba(0, 160, 193, 0.02)');

      new Chart(this.$refs.upload_data_chart, {
        type: 'line',
        data: {
          labels: this.fileSizeData.labels,
          datasets: [
            {
              label: "Data in Kilobytes (Kb)",
              data: resp.data.file_count,
              backgroundColor: uploadGrad,
              borderColor: "#00A0C1",
              borderWidth: 2.5,
              pointBackgroundColor: "#00A0C1",
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: "#00A0C1",
              pointHoverBorderWidth: 3,
            }
          ]
        },
        options: {
          responsive: true,
          lineTension: 0.4,
          animation: {
            duration: 800,
            easing: 'easeOutQuart'
          },
          legend: {
            labels: {
              fontSize: 12,
              fontColor: '#435666',
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 16
            }
          },
          tooltips: {
            backgroundColor: 'rgba(44, 62, 80, 0.9)',
            titleFontSize: 13,
            bodyFontSize: 12,
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
            mode: 'index',
            intersect: false,
          },
          hover: {
            mode: 'index',
            intersect: false
          },
          scales: {
            xAxes: [
              {
                ticks: {
                  minRotation: fc > 10 ? 50 : 0,
                  fontColor: '#6c757d',
                  fontSize: 11,
                },
                gridLines: {
                  display: false
                }
              }
            ],
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  padding: 15,
                  fontColor: '#6c757d',
                  fontSize: 11,
                },
                gridLines: {
                  color: 'rgba(0, 0, 0, 0.05)',
                  drawBorder: false
                }
              }
            ]
          }
        }
      })
    },

    async getReportedWebsites() {

      if (this.useDummyData) {
        this.reportedWebsites = [
          { id: 1, account_name: 'Acme Corp', website: 'http://malware-site.xyz', user_agent: 'Chrome/120.0', api_key: 'ext_a1b2c3d4' },
          { id: 2, account_name: 'Tech Solutions', website: 'http://phishing-login.com/steal', user_agent: 'Firefox/119.0', api_key: 'ext_e5f6g7h8' },
          { id: 3, account_name: 'Acme Corp', website: 'http://suspicious-download.net', user_agent: 'Edge/120.0', api_key: 'ext_i9j0k1l2' },
          { id: 4, account_name: 'Global Inc', website: 'http://fake-bank.org/login', user_agent: 'Chrome/120.0', api_key: 'ext_m3n4o5p6' },
        ]
        return
      }

      if(!this.$store.state.auth.isSuperAdmin) {
        this.reportedWebsites = []
        return
      }
        
      let apiPath = "dashboard/" + this.$store.state.auth.organisation_uuid + "/get-reported-websites-for-dashboard"
      await this.$axios.get(apiPath)
        .then((response) => {

          if (response.data.account_reported_websites === "No Record Found")
            this.reportedWebsites = []
          else {
            this.reportedWebsites = response.data.reported_websites
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },


    async fetchLogsData() {
      try {

        let resp = null

        if (this.useDummyData) {
          resp = { data: {
            labels: ['14-Apr', '15-Apr', '16-Apr', '17-Apr', '18-Apr', '19-Apr', '20-Apr', '21-Apr', '22-Apr', '23-Apr'],
            allow_count: [85, 120, 95, 145, 110, 160, 130, 90, 155, 140],
            block_count: [12, 28, 15, 35, 22, 18, 40, 25, 30, 20]
          }}
        } else {
          resp = await this.$axios.get("dashboard/" + this.$store.state.auth.organisation_uuid + "/get-account-logs-data")
        }

        if (resp.data.success != null && resp.data.success == false) {
          this.labels_blocked_allowed_websites = null
        } else {
          this.labels_blocked_allowed_websites = resp.data.labels.map(l => this.formatChartLabel(l))
        }

        if (this.labels_blocked_allowed_websites != null) {
          if (this.labels_blocked_allowed_websites.length <= 0) {
            this.labels_blocked_allowed_websites.labels = ["Empty data"]
          }
          if (resp.data.allow_count == null) {
            resp.data.allow_count = [0]
          }

          if (resp.data.block_count == null) {
            resp.data.block_count = [0]
          }

          // Calculate totals for stat cards
          this.totalAllowed = resp.data.allow_count.reduce((a, b) => a + b, 0)
          this.totalBlocked = resp.data.block_count.reduce((a, b) => a + b, 0)
        }

        this.rawLogsResp = resp

      } catch (error) {
        console.log(error)
      }
    },

    renderLogsChart() {
      if (!this.rawLogsResp || !this.$refs.block_and_allowed_chart) return
      const resp = this.rawLogsResp

      var barCtx = this.$refs.block_and_allowed_chart.getContext('2d');
      var allowedGrad = barCtx.createLinearGradient(0, 0, 0, 290);
      allowedGrad.addColorStop(0, 'rgba(0, 160, 193, 0.85)');
      allowedGrad.addColorStop(1, 'rgba(0, 160, 193, 0.4)');
      var blockedGrad = barCtx.createLinearGradient(0, 0, 0, 290);
      blockedGrad.addColorStop(0, 'rgba(243, 153, 77, 0.85)');
      blockedGrad.addColorStop(1, 'rgba(243, 153, 77, 0.4)');

      new Chart(this.$refs.block_and_allowed_chart, {
        type: 'bar',
        data: {
          labels: this.labels_blocked_allowed_websites,
          datasets: [
            {
              label: "Allowed",
              data: resp.data.allow_count,
              backgroundColor: allowedGrad,
              borderColor: "#00A0C1",
              borderWidth: 0,
              hoverBackgroundColor: '#00A0C1',
            },
            {
              label: "Blocked",
              data: resp.data.block_count,
              backgroundColor: blockedGrad,
              borderColor: "#F3994D",
              borderWidth: 0,
              hoverBackgroundColor: '#F3994D',
            },
          ]
        },
        options: {
          responsive: true,
          animation: {
            duration: 800,
            easing: 'easeOutQuart'
          },
          legend: {
            labels: {
              fontSize: 12,
              fontColor: '#435666',
              usePointStyle: true,
              pointStyle: 'rectRounded',
              padding: 16
            }
          },
          tooltips: {
            backgroundColor: 'rgba(44, 62, 80, 0.9)',
            titleFontSize: 13,
            bodyFontSize: 12,
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
            mode: 'index',
            intersect: false,
          },
          scales: {
            xAxes: [
              {
                barPercentage: 0.6,
                stacked: true,
                ticks: {
                  fontColor: '#6c757d',
                  fontSize: 11,
                },
                gridLines: {
                  display: false
                }
              }
            ],
            yAxes: [
              {
                stacked: true,
                ticks: {
                  beginAtZero: true,
                  padding: 15,
                  fontColor: '#6c757d',
                  fontSize: 11,
                },
                gridLines: {
                  color: 'rgba(0, 0, 0, 0.05)',
                  drawBorder: false
                }
              }
            ]
          }
        }
      })
    },

    async getXSurflogOrganisations() {
      await this.$axios.get("/admin-common/get-xsurflog-organisations")
        .then((response) => {

          if (response.data.organisations.length > 0) {
            this.$store.commit("auth/setOrganisations", response.data.organisations)
            if (this.$store.state.auth.organisation == null || this.$store.state.auth.organisation == undefined) {

              if (this.$store.state.auth.organisations.length > 0) {
                this.$store.commit("auth/setOrganisation", response.data.organisations[0])
                this.CheckAccount(this.$store.state.auth.organisation.orguuid, this.$store.state.auth.organisation.name)
              }

            }
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },

    async CheckAccount(OrgUuid, Name) {
      try {

        const payLoad = {
          org_uuid: OrgUuid,
          name: Name
        }

        fetch(process.env.VUE_APP_API_BASE_URL + "/api/main/v1/auth/non-login/check-account-exist", {
          method: "POST",
          body: JSON.stringify(payLoad),
          headers: {
            "Content-type": "application/json; charset=UTF-8"
          }
        });

      } catch (error) {
        console.log(error)
      }
    },


    formLabels(data) {

      console.log(data)

      let monthInt = 0

      if (data != null) {

        console.log("nag sulod d")

        for (let x = 0; x < data.data.length; x++) {
          console.log(data.data[x])
        }
      }


      this.labels_blocked_allowed_websites.push(this.getMonth(monthInt))
    },

    getLabels() {
      this.labels = []
      let baseIntMonth = moment().month()

      baseIntMonth = baseIntMonth - 2

      if (baseIntMonth == -2) {
        baseIntMonth = 10
      } else if (baseIntMonth == -1) {
        baseIntMonth = 11
      }

      for (let x = 1; x < 4; x++) {
        this.labels.push(this.getMonth(baseIntMonth))
        baseIntMonth++
        if (baseIntMonth > 11) {
          baseIntMonth = 0
        }
      }

    },

    truncateText(text, limit) {

      if (text === null)
        return
      if (text === '')
        return

      if (text.length <= limit) {
        return text;
      } else {
        return text.slice(0, limit) + '...';
      }
    },

    trimQuestionMark(text) {
      let index = text.indexOf('?')

      if (index > 0) {
        return text.substring(0, index)
      } else {
        return text
      }
    },

    async markStatus(website, is_safe) {
      try {

        const payload = {
          id: website.id,
          domain: website.website,
          is_safe: is_safe
        }

        const resp = await this.$axios.post("admin-controls/mark-reported-website", payload)

        if (resp.data.success != null && resp.data.success == false) {
          this.$toast.error(resp.data.message || "Failed to update reported website status.")
          this.getReportedWebsites()
        } else {
          this.$toast.success(resp.data.message)
          this.getReportedWebsites()
        }

      } catch (error) {
        console.log(error)
        this.$toast.error(extractErrorMessage(error, "Failed to update reported website status."))
        this.getReportedWebsites()
      }
    },
  },
}
</script>

<style scoped>
/* ─── Transition ─── */
.dashboard-fade-enter-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.dashboard-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}

/* ─── Skeleton Loading ─── */
@keyframes shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}

.skeleton-shimmer {
  background: linear-gradient(
    90deg,
    #e9ecef 25%,
    #f4f6f8 37%,
    #e9ecef 63%
  );
  background-size: 800px 100%;
  animation: shimmer 1.6s ease-in-out infinite;
}

.skeleton-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
}

.skeleton-text {
  border-radius: 4px;
}

.skeleton-text-lg {
  height: 22px;
  width: 60px;
}

.skeleton-text-md {
  height: 14px;
}

.skeleton-text-sm {
  height: 10px;
  width: 80px;
}

.skeleton-chart {
  border-radius: 8px;
}

.skeleton-circle {
  width: 180px;
  height: 180px;
  border-radius: 50%;
}

.skeleton-table-row {
  height: 36px;
  border-radius: 6px;
  margin-bottom: 8px;
}

/* ─── Stat Cards ─── */
.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  height: calc(100% - 12px);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
}

.stat-label {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ─── Chart Cards ─── */
.chart-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-bottom: 12px;
  height: calc(100% - 12px);
  display: flex;
  flex-direction: column;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 8px;
}

.chart-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #435666;
}

.chart-body {
  padding: 8px 16px 16px;
  flex: 1;
}

/* ─── Action Buttons ─── */
.action-btn {
  border-radius: 6px !important;
  width: 26px;
  height: 26px;
  padding: 0;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ─── Table Styling ─── */
.table th {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #e9ecef;
}

.table td {
  color: #435666;
  padding: 8px;
}

/* ─── Empty State ─── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  padding: 32px 16px;
  text-align: center;
}

.empty-state-icon {
  font-size: 40px;
  color: #ced4da;
  margin-bottom: 14px;
}

.empty-state-text {
  font-size: 15px;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 4px;
}

.empty-state-subtext {
  font-size: 12px;
  color: #adb5bd;
  max-width: 220px;
  line-height: 1.4;
}
</style>